import {useEffect, useState} from 'react';
import {Box} from '@mui/material';
import {useTranslation} from 'react-i18next';
import {matchPath, useLocation, useParams, useRouteMatch} from 'react-router-dom';
import {CaregiverRoutes} from '../../../../../../components/caregiver-routes/caregiver-routes';
import {LoadingContent} from '../../../../../../components/loading-content/loading-content';
import {SectionTabs} from '../../../../../../components/section-tabs/section-tabs';
import {User} from '../../../../../../backend/interfaces/user';
import {CaregiverMode} from '../../../../../../enums';
import {translations} from '../../../../../../i18n/translations';
import {UsersRepository} from '../../../../../../repositories/users';
import {useOffice} from '../../../../../../contexts/office/context';

enum CaregiverTabs {
  Profile = 'profile',
  Affiliations = 'affiliations',
}

export default function CaregiverSystemAdministratorOfficeScreen() {
  const {id}: {id?: string} = useParams();
  const {url} = useRouteMatch();
  const location = useLocation();
  const {t} = useTranslation();
  const officeContext = useOffice();
  const [caregiver, setCaregiver] = useState<User>();
  const [isLoading, setIsLoading] = useState(true);

  const profileUrl = `${url}/${CaregiverTabs.Profile}`;
  const affiliationsUrl = `${url}/${CaregiverTabs.Affiliations}`;
  const caregiverTitle =
    caregiver?.displayName ||
    caregiver?.name ||
    officeContext.state.userForm?.user.displayName ||
    officeContext.state.userForm?.user.name ||
    '';
  const currentTab = matchPath(location.pathname, {
    path: profileUrl,
    exact: true,
    strict: false,
  })
    ? CaregiverTabs.Profile
    : matchPath(location.pathname, {
          path: affiliationsUrl,
          exact: true,
          strict: false,
        })
      ? CaregiverTabs.Affiliations
      : undefined;

  useEffect(() => {
    let isCurrent = true;

    const getCaregiver = async () => {
      if (!id || caregiver?.id === id) {
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      if (caregiver?.id !== id) {
        setCaregiver(undefined);
      }

      try {
        const result = await UsersRepository.instance.getById(id);
        if (isCurrent) {
          setCaregiver(result);
        }
      } catch (error) {
        console.debug('🔴 Could not get caregiver for detail tabs.', error);
      } finally {
        if (isCurrent) {
          setIsLoading(false);
        }
      }
    };

    getCaregiver();

    return () => {
      isCurrent = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, currentTab]);

  return (
    <Box
      sx={{
        background: '#FFFFFF',
        width: '100%',
        borderRadius: '10px',
      }}
    >
      {currentTab && (
        <Box
          sx={{
            width: '100%',
            maxWidth: '1024px',
            marginX: 'auto',
            marginBottom: '16px',
            paddingX: {xs: '12px', sm: '24px'},
            boxSizing: 'border-box',
          }}
        >
          {!caregiverTitle && <Box aria-hidden sx={{height: '96px'}} />}
          <SectionTabs
            title={caregiverTitle}
            titleAvatar={{
              type: 'user',
              url: caregiver?.picture || officeContext.state.userForm?.user.picture,
            }}
            currentTab={currentTab}
            tabs={[
              {
                value: CaregiverTabs.Profile,
                label: t(translations.caregiver_tabs.tabs.profile),
                href: profileUrl,
              },
              {
                value: CaregiverTabs.Affiliations,
                label: t(translations.caregiver_tabs.tabs.affiliations),
                href: affiliationsUrl,
              },
            ]}
            exitUrl={url.slice(0, url.lastIndexOf('/'))}
          />
        </Box>
      )}
      {currentTab && isLoading ? (
        <LoadingContent marginTop="24px" sx={{minHeight: 'calc(100vh - 400px)'}} />
      ) : (
        <CaregiverRoutes caregiverMode={CaregiverMode.SystemAdministrator} />
      )}
    </Box>
  );
}
